// Le flot de `wheel` découpé en gestes, et ce qui autorise un geste à faire basculer la page (contrôleur
// continuous-pages, docs/frontend/continuous_pages.md). Sans DOM : le contrôleur lui donne les écarts, les instants et
// ce qu'il voit (bout de page, défilement).
//
// Un geste ne bascule que s'il est délibéré :
//   - il commence en bout de page, après un vrai silence (pas une relance au milieu de l'inertie du pavé tactile) ;
//   - la page était au repos : rien n'a défilé depuis EDGE_REST ms. L'inertie qui amène en bas de page continue
//     d'émettre des `wheel` qui ne doivent pas enchaîner sur la suivante (pavé tactile) ;
//   - il pousse assez loin au-delà du bord : un cran de molette (un premier écart d'au moins NOTCH px) suffit, un
//     pavé tactile doit glisser de DELIBERATE_TRAVEL px.

// Silence (ms) qui termine un geste de molette.
export const GESTURE_GAP = 200
// Repos (ms) en bout de page avant qu'un geste puisse basculer.
export const EDGE_REST = 300
// Premier écart (px) d'un cran de molette ; un pavé tactile commence bien en dessous.
export const NOTCH = 40
// Distance (px) à pousser au-delà du bord : un cran de molette, un glissé franc du pavé tactile.
export const WHEEL_TRAVEL = 12
export const DELIBERATE_TRAVEL = 100

export class WheelGestures {
  constructor() {
    this.gesture = null
    this.lastScrollAt = -Infinity
  }

  // Ce qui défile a bougé à cet instant (événement `scroll`).
  scrolled(time) {
    this.lastScrollAt = time
  }

  // Un `wheel` : renvoie vrai s'il ouvre un geste. Un geste commence après un silence, à un changement de sens
  // (opener « pause »), ou quand les écarts remontent nettement après avoir décru (« rebound ») : une nouvelle poussée
  // au milieu de l'inertie de la précédente, le pavé tactile ne disant pas quand le doigt se repose.
  track(delta, time) {
    const magnitude = Math.abs(delta)
    const sign = Math.sign(delta)
    const gesture = this.gesture
    const pause = !gesture || time - gesture.lastAt > GESTURE_GAP || sign !== gesture.sign
    const rebound = !pause && gesture.decaying && magnitude > gesture.floor * 1.6 + 2

    if (pause || rebound) {
      this.gesture = {
        sign, lastAt: time, startedAt: time, peak: magnitude, floor: magnitude, decaying: false,
        opener: pause ? "pause" : "rebound", notch: magnitude >= NOTCH, fromEdge: false, travel: 0,
      }
      return true
    }

    gesture.lastAt = time
    gesture.peak = Math.max(gesture.peak, magnitude)
    if (!gesture.decaying && magnitude < gesture.peak / 2) {
      gesture.decaying = true
      gesture.floor = magnitude
    }
    if (gesture.decaying) gesture.floor = Math.min(gesture.floor, magnitude)
    return false
  }

  // Au premier `wheel` du geste : peut-il faire basculer ? (bout de page, silence, page au repos)
  arm(atEdge) {
    const gesture = this.gesture
    gesture.fromEdge = atEdge && gesture.opener === "pause" && gesture.startedAt - this.lastScrollAt >= EDGE_REST
  }

  // Le geste ne basculera pas (un bloc intérieur le prend, la page a déjà basculé).
  disarm() {
    if (this.gesture) this.gesture.fromEdge = false
  }

  get armed() {
    return Boolean(this.gesture?.fromEdge)
  }

  // Un `wheel` poussé au-delà du bord par un geste armé : vrai quand il est allé assez loin pour basculer.
  push(delta) {
    const gesture = this.gesture
    gesture.travel += Math.abs(delta)
    if (gesture.travel < (gesture.notch ? WHEEL_TRAVEL : DELIBERATE_TRAVEL)) return false

    gesture.fromEdge = false
    return true
  }
}
